import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";

import {
  MetaDivider,
  MetaEmptyRow,
  MetaListItem,
  MetaPanel,
  MetaRow,
  MetaSection,
  useMetaPanelCollapse,
} from "../../../../../components/shared";
import {
  PatientTreatmentPlan,
  PatientTreatmentPlanStageLog,
} from "../../../../../types/healthcare/TreatmentPlanTypes";
import { Medication } from "../../../../../types/healthcare/MedicationTypes";
import { MedicalHistory } from "../../../../../types/healthcare/MedicalHistoryTypes";
import { historyTypesOptions } from "../../../../../types/healthcare/MedicalHistoryTypes";
import { fetchActivePatientMedications } from "../../../../../api/healthcare/MedicationsApi";
import { fetchActiveMedicalHistories } from "../../../../../api/healthcare/PatientApi";
import { fetchAppointments } from "../../../../../api/healthcare/Appointment/AppointmentApi";
import { getAssignedForms, AssignedForm } from "../../../../../api/formApi";
import { isFormDone } from "../../../../../utils/forms/formStatus";
import ReviewFormModal from "../../../formsInTray/ReviewFormModal";
import { computeProgress } from "./workflowProgress/planProgress";
import type { UserUuid } from "../../../../../types/identity";

// ── Types ────────────────────────────────────────────────────────────────────

interface PatientMetaPanelProps {
  plan: PatientTreatmentPlan;
  stageLogs: PatientTreatmentPlanStageLog[];
}

// ── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  Peptides:    "#5b21b6",
  Genetics:    "#0369a1",
  Wellness:    "#065f46",
  Supplements: "#92400e",
};

const DEFAULT_CATEGORY_COLOR = "#374151";

const MAX_LIST_ITEMS = 5;

const COLLAPSE_STORAGE_KEY = "mg-planmeta-collapsed";

const HISTORY_LABELS: Record<string, string> = historyTypesOptions.reduce(
  (acc, o) => { acc[o.value] = o.label; return acc; },
  {} as Record<string, string>,
);

// ── Helpers ──────────────────────────────────────────────────────────────────

function deriveAge(dateOfBirth: Date | null | undefined): string {
  if (!dateOfBirth) return "—";
  const age = dayjs().diff(dayjs(dateOfBirth), "year");
  return isNaN(age) || age < 0 ? "—" : String(age);
}

function formatSex(sex: string | undefined, gender: string | undefined): string {
  const value = sex || gender;
  if (!value) return "—";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getCategoryColor(category: string | undefined): string {
  if (!category) return DEFAULT_CATEGORY_COLOR;
  return CATEGORY_COLORS[category] ?? DEFAULT_CATEGORY_COLOR;
}

function historyLabel(type: string): string {
  return HISTORY_LABELS[type] ?? type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Component ────────────────────────────────────────────────────────────────

const PatientMetaPanel: React.FC<PatientMetaPanelProps> = ({ plan, stageLogs }) => {
  const patient = plan.patient;
  const template = plan.template;
  const patientId: UserUuid | undefined = patient?.id ?? plan.patient_id;
  const company = plan.company;

  // ── Live patient clinical context (mirrors the patient modal's overview) ──
  const { data: medsData, isLoading: medsLoading } = useQuery({
    queryKey: ["planMeta", "medications", patientId],
    queryFn: () => fetchActivePatientMedications(patientId as UserUuid),
    enabled: !!patientId,
    staleTime: 60_000,
  });
  const medications: Medication[] = medsData?.data ?? [];

  const { data: histData, isLoading: histLoading } = useQuery({
    queryKey: ["planMeta", "medicalHistory", patientId],
    queryFn: () => fetchActiveMedicalHistories(patientId as UserUuid),
    enabled: !!patientId,
    staleTime: 60_000,
  });
  const histories: MedicalHistory[] = histData ?? [];

  const { data: apptData, isLoading: apptLoading } = useQuery({
    queryKey: ["planMeta", "appointments", patientId],
    queryFn: () =>
      fetchAppointments(new Date(), dayjs().add(120, "day").toDate(), undefined, patientId),
    enabled: !!patientId && !!company,
    staleTime: 60_000,
  });

  const upcomingAppointment = useMemo(() => {
    const list = apptData?.data ?? [];
    return list
      .filter((a) => a.status?.toLowerCase() !== "cancelled")
      .map((a) => ({ appt: a, when: a.start_time }))
      .filter((x) => x.when && dayjs(x.when).isAfter(dayjs()))
      .sort((p, q) => dayjs(p.when).valueOf() - dayjs(q.when).valueOf())[0]?.appt ?? null;
  }, [apptData]);

  const { data: formsData, isLoading: formsLoading } = useQuery({
    queryKey: ["planMeta", "forms", patientId],
    queryFn: () => getAssignedForms(patientId as UserUuid),
    enabled: !!patientId,
    staleTime: 60_000,
  });

  const patientForms = (formsData ?? []).filter(
    (f) => f.assignee_type === "patient" && isFormDone(f.status)
  );

  const [previewForm, setPreviewForm] = useState<AssignedForm | null>(null);

  const [collapsed, toggle] = useMetaPanelCollapse(COLLAPSE_STORAGE_KEY);

  const age = deriveAge(patient?.date_of_birth);
  const sex = formatSex(patient?.sex, patient?.gender);
  const ageSex = age !== "—" || sex !== "—" ? `${age} / ${sex}` : "—";
  const dob = patient?.date_of_birth ? dayjs(patient.date_of_birth).format("D MMM YYYY") : "—";

  const weight = patient?.weight ? `${patient.weight} kg` : "—";
  const height = patient?.height ? `${patient.height} cm` : "—";

  const category = template?.category;
  const categoryColor = getCategoryColor(category);

  const sectionName = plan.current_section?.name;
  const sectionColor = plan.current_section?.color;

  const { completed, total } = computeProgress(plan, stageLogs);
  const progressLabel = total > 0 ? `${completed} / ${total} stages` : "—";

  const startedAt = plan.created_at
    ? dayjs(plan.created_at).format("D MMM YYYY")
    : "—";

  return (
    <>
    <MetaPanel>

      {/* Patient section */}
      <MetaSection title="Patient" open={!collapsed["patient"]} onToggle={() => toggle("patient")}>
        <MetaRow label="Age / Sex" value={ageSex} />
        <MetaRow label="Date of Birth" value={dob} />
        <MetaRow label="Weight" value={weight} />
        <MetaRow label="Height" value={height} />
        {patient?.phone && <MetaRow label="Phone" value={patient.phone} />}
        {patient?.email && <MetaRow label="Email" value={patient.email} />}
      </MetaSection>

      <MetaDivider />

      {/* Upcoming appointment */}
      <MetaSection title="Upcoming Appointment" open={!collapsed["appointment"]} onToggle={() => toggle("appointment")}>
        {apptLoading ? (
          <MetaEmptyRow>Loading…</MetaEmptyRow>
        ) : upcomingAppointment ? (
          <MetaListItem
            primary={upcomingAppointment.appointment_type?.name ?? upcomingAppointment.title ?? "Appointment"}
            secondary={dayjs(upcomingAppointment.start_time).format("ddd D MMM YYYY, h:mma")}
          />
        ) : (
          <MetaEmptyRow>None scheduled</MetaEmptyRow>
        )}
      </MetaSection>

      <MetaDivider />

      {/* Active medications */}
      <MetaSection title="Active Medications" count={medications.length} open={!collapsed["medications"]} onToggle={() => toggle("medications")}>
        {medsLoading ? (
          <MetaEmptyRow>Loading…</MetaEmptyRow>
        ) : medications.length === 0 ? (
          <MetaEmptyRow>None recorded</MetaEmptyRow>
        ) : (
          <>
            {medications.slice(0, MAX_LIST_ITEMS).map((m) => (
              <MetaListItem
                key={m.id}
                primary={m.medication_name}
                secondary={[m.dosage, m.frequency].filter(Boolean).join(" · ")}
              />
            ))}
            {medications.length > MAX_LIST_ITEMS && (
              <MetaEmptyRow>+{medications.length - MAX_LIST_ITEMS} more</MetaEmptyRow>
            )}
          </>
        )}
      </MetaSection>

      <MetaDivider />

      {/* Medical history */}
      <MetaSection title="Medical History" count={histories.length} open={!collapsed["history"]} onToggle={() => toggle("history")}>
        {histLoading ? (
          <MetaEmptyRow>Loading…</MetaEmptyRow>
        ) : histories.length === 0 ? (
          <MetaEmptyRow>None recorded</MetaEmptyRow>
        ) : (
          <>
            {histories.slice(0, MAX_LIST_ITEMS).map((h) => (
              <MetaListItem
                key={h.id}
                primary={historyLabel(h.history_type)}
                secondary={[h.description, h.severity ? `Severity: ${h.severity}` : ""].filter(Boolean).join(" — ")}
              />
            ))}
            {histories.length > MAX_LIST_ITEMS && (
              <MetaEmptyRow>+{histories.length - MAX_LIST_ITEMS} more</MetaEmptyRow>
            )}
          </>
        )}
      </MetaSection>

      <MetaDivider />

      {/* Patient forms */}
      <MetaSection title="Patient Forms" count={patientForms.length} open={!collapsed["forms"]} onToggle={() => toggle("forms")}>
        {formsLoading ? (
          <MetaEmptyRow>Loading…</MetaEmptyRow>
        ) : patientForms.length === 0 ? (
          <MetaEmptyRow>None completed</MetaEmptyRow>
        ) : (
          <>
            {patientForms.slice(0, MAX_LIST_ITEMS).map((f) => {
              const when = f.reviewed_at ?? f.updated_at;
              return (
                <MetaListItem
                  key={f.id}
                  primary={f.form?.title ?? "Form"}
                  secondary={when ? dayjs(when).format("D MMM YYYY") : ""}
                  onClick={() => setPreviewForm(f)}
                />
              );
            })}
            {patientForms.length > MAX_LIST_ITEMS && (
              <MetaEmptyRow>+{patientForms.length - MAX_LIST_ITEMS} more</MetaEmptyRow>
            )}
          </>
        )}
      </MetaSection>

      <MetaDivider />

      {/* Plan section */}
      <MetaSection title="Plan" open={!collapsed["plan"]} onToggle={() => toggle("plan")}>
        <MetaRow
          label="Category"
          value={
            category ? (
              <span
                style={{
                  display: "inline-block",
                  padding: "1px 6px",
                  borderRadius: 3,
                  background: categoryColor,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                }}
              >
                {category}
              </span>
            ) : (
              "—"
            )
          }
        />

        <MetaRow
          label="Section / Phase"
          value={
            sectionName ? (
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {sectionColor && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: sectionColor,
                      flexShrink: 0,
                      display: "inline-block",
                    }}
                  />
                )}
                {sectionName}
              </span>
            ) : (
              "—"
            )
          }
        />

        <MetaRow label="Progress" value={progressLabel} />
        <MetaRow label="Started" value={startedAt} />
      </MetaSection>
    </MetaPanel>

    {previewForm && (
      <ReviewFormModal
        isOpen={!!previewForm}
        onClose={() => setPreviewForm(null)}
        onSave={() => setPreviewForm(null)}
        assignedForm={previewForm}
      />
    )}
    </>
  );
};

export default PatientMetaPanel;
